import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FilterPropsInterface} from "@/views/Admin/Events/Event/Promocodes/Filter/Filter.props.interface";
import {Button, Checkbox, Input, Suggest} from "uikit";
import styles from './Filter.module.scss'
import CrossIcon from "@/assets/images/icons/12/close.svg";
import {FilterSchema} from "@/views/Admin/Events/Event/Promocodes/Filter/Filter.schema";
import {useQueryRouter} from "@/hooks/useQueryRouter/useQueryRouter";
import {FormFields} from "@/utils/FormFields/FormFields";
import {PromocodesSchema} from "@/views/Admin/Events/Event/Promocodes/Promocodes.schema";
import {useLocation, useRouteMatch} from "react-router-dom";
import {EventBasesService} from "@/services/EventBasesService/EventBasesService";
import {PromocodeGroupsService} from "@/services/PromocodeGroupsService/PromocodeGroupsService";

const KEY_MAP: Record<string, string> = {eventId: 'eventIds', groupId: 'groupIds', promocodeId: 'promocodeIds'};
const KEY_REVERSE: Record<string, string> = Object.fromEntries(Object.entries(KEY_MAP).map(([s, p]) => [p, s]));
const MULTI_FIELDS = ['eventId', 'groupId', 'promocodeId'];

const getFilter = (data) => {
    return Object.fromEntries(
        Object.entries(data || {})
            .filter(([k]: object) => (KEY_REVERSE[k] ?? k) in FilterSchema.fields)
            .map(([k, v]) => {
                const sk = KEY_REVERSE[k] ?? (k.endsWith('Ids') ? k.slice(0, -1) : k);
                if (sk === 'active') return [sk, v === 'true'];
                if (MULTI_FIELDS.includes(sk)) return [sk, Array.isArray(v) ? v : String(v).split(',')];
                return [sk, v];
            })
    );
}

const Filter = ({data, onReset, onSubmit}: FilterPropsInterface) => {
        const {query, set} = useQueryRouter();
        const {tabs} = data;
        const {search} = useLocation();
        const params = useMemo(() => new URLSearchParams(search), [search]);
        const content = tabs.includes(params.get('content') as string) ? params.get('content') : 'promocodes';
        const filter: object = getFilter(query.filter)
        const fields = FormFields(FilterSchema.fields).setKey('value', filter).build()
        const {isLoading} = data;
        const [form, setForm] = useState({...FilterSchema, fields});
        const {promocode, name, groupId, eventId, active} = form.fields;
        const {groups, events} = form.data
        const match = useRouteMatch()
        const event_id = String(match.params.event_id) || ''
        const [keyword, setKeyword] = useState('');
        const queryTargetRef = useRef<'groups' | 'events' | null>(null);

        useEffect(() => {
            const h = setTimeout(() => {
                if (!queryTargetRef.current) return;
                if (queryTargetRef.current === 'groups') {
                    const params = keyword ? {filter: {name: keyword}} : undefined;
                    handleGetLists({key: 'groups', fn: PromocodeGroupsService.GetList, num: 1, params});
                }
                if (queryTargetRef.current === 'events') {
                    const params = keyword ? {q: keyword} : undefined;
                    handleGetLists({key: 'events', fn: EventBasesService.GetList, num: 1, params});
                }
            }, 300);
            return () => clearTimeout(h);
        }, [keyword]);

        useEffect(() => {
            handleGetLists({key: 'events', fn: EventBasesService.GetList, num: 1})
            content === 'promocodes' && handleGetLists({key: 'groups', fn: PromocodeGroupsService.GetList, num: 1})
        }, []);

        const handleChange = (field, value) => {
            setForm(prev => ({...prev, fields: {...prev.fields, [field]: {...prev.fields[field], value}}}))
        }

        const handleSubmit = (e) => {
            e.preventDefault();
            const filter = Object.fromEntries(
                Object.entries(FormFields(form.fields).getKeyValue())
                    .filter(([, v]) => v !== null && v !== undefined && v !== '')
                    .map(([k, v]) => [
                        KEY_MAP[k] ?? (k.endsWith('Id') ? `${k}s` : k),
                        Array.isArray(v)
                            ? v.map(val => String(val).replace(/^id:/, ''))
                            : String(v).replace(/^id:/, '')
                    ])
            );
            set({
                filter,
                page: {
                    number: PromocodesSchema.promocodes.meta.currentPage,
                    size: query.page.size || PromocodesSchema.promocodes.meta.perPage
                }
            });
        }

        const handleReset = async (e) => {
            e.preventDefault();
            setForm(prev => ({
                ...prev,
                fields: FormFields(prev.fields).setKey('value', null).build()
            }));
            set({
                filter: {},
                page: {number: PromocodesSchema.promocodes.meta.currentPage, size: PromocodesSchema.promocodes.meta.perPage}
            });
        }

        const handleGetLists = async ({key, fn, num, params}: {
            key: 'events' | 'groups',
            fn: (p?: any) => Promise<any>,
            num: number,
            params?: Record<string, any>
        }) => {
            setForm(prev => ({...prev, data: {...prev.data, [key]: {...prev.data[key], isLoading: true}}}));

            try {
                const {data, meta} = await fn({
                    page: {number: num, size: PromocodesSchema.promocodes.meta.perPage},
                    ...(params || {})
                });

                setForm(prev => ({
                    ...prev,
                    data: {
                        ...prev.data,
                        [key]: {
                            ...prev.data[key],
                            meta,
                            data: num === 1
                                ? data
                                : [
                                    ...prev.data[key].data,
                                    ...data.filter((n) => !prev.data[key].data.some((o) => String(o.id) === String(n.id)))
                                ],
                        }
                    }
                }))
            } catch (e) {
                console.error(e)
            } finally {
                setForm(prev => ({...prev, data: {...prev.data, [key]: {...prev.data[key], isLoading: false}}}));
            }
        }

        const handleReachEnd = async (key) => {
            const {currentPage = 0, lastPage = 1} = form.data[key].meta || {};
            const num = currentPage + 1
            if (form.data[key]?.isLoading || currentPage >= lastPage) return;

            if (key === 'groups') {
                const params = queryTargetRef.current === 'groups' && keyword ? {filter: {name: keyword}} : undefined;
                await handleGetLists({key, fn: PromocodeGroupsService.GetList, num, params});
            }

            if (key === 'events') {
                const params = queryTargetRef.current === 'events' && keyword ? {q: keyword} : undefined;
                await handleGetLists({key, fn: EventBasesService.GetList, num, params});
            }
        }

        const renderPlaceholderSuggest = (field) => {
            const value = Array.isArray(field.value) ? field.value.filter(x => String(x) !== event_id).filter(Boolean).length : 0

            return [
                `Выбрано ${field === eventId ? 'событий' : 'групп'}:`,
                value
            ].join(' ')
        }


        return (
            <form className={styles.container} onSubmit={handleSubmit}>
                <div className={styles.filter}>
                    <div className={styles.field}>
                        {content === 'promocodes' && <Input
                            className={styles.field}
                            size={'small'}
                            label={promocode.label}
                            error={promocode.error}
                            placeholder={promocode.placeholder}
                            disabled={isLoading || promocode.isDisabled || promocode.isLoading}
                            value={promocode.value}
                            isClearable={true}
                            onChange={({target}) => handleChange('promocode', target.value || null)}
                        />}
                        {content === 'groups' && <Input
                            className={styles.field}
                            size={'small'}
                            label={name.label}
                            error={name.error}
                            placeholder={name.placeholder}
                            disabled={isLoading || name.isDisabled || name.isLoading}
                            value={name.value}
                            isClearable={true}
                            onChange={({target}) => handleChange('name', target.value || null)}
                        />}
                    </div>
                    {content === 'promocodes' && <div className={styles.field}>
                        <Suggest
                            size={'small'}
                            label={groupId.label}
                            error={groupId.error}
                            disabled={isLoading || groupId.isDisabled || groupId.isLoading || form.isLoading}
                            isMulti={true}
                            className={(groupId.value && groupId.value.length) && styles.placeholder}
                            selectedValues={Array.isArray(groupId.value) ? groupId.value.map(v => `id:${v}`) : `id:${groupId.value}`}
                            value={queryTargetRef.current === 'groups' ? keyword : ''}
                            placeholder={(groupId.value && groupId.value.length) ? renderPlaceholderSuggest(groupId) : groupId.placeholder}
                            data={Object.fromEntries((groups.data || []).map(e => [`id:${e.id}`, e.name]))}
                            onInput={({target}) => setKeyword(target.value || '')}
                            onChange={(val) => {
                                const mapped = Array.isArray(val)
                                    ? val.filter(v => v && v !== 'id:null').map(v => v.replace(/^id:/, ''))
                                    : val.replace(/^id:/, '');
                                handleChange('groupId', mapped);
                                setKeyword('');
                                handleGetLists({key: 'groups', fn: PromocodeGroupsService.GetList, num: 1});
                            }}
                            onReachEnd={() => handleReachEnd('groups')}
                            onDropShow={() => {
                                queryTargetRef.current = 'groups';
                                setKeyword('');
                            }}
                            onDropHide={() => {
                                setKeyword('');
                                queryTargetRef.current = null;
                            }}
                        />
                    </div>}
                    <div className={styles.field}>
                        <Suggest
                            size={'small'}
                            label={eventId.label}
                            error={eventId.error}
                            disabled={isLoading || eventId.isDisabled || eventId.isLoading || form.isLoading}
                            isMulti={true}
                            className={(Array.isArray(eventId.value) && eventId.value.filter(x => String(x) !== event_id).length > 0) && styles.placeholder}
                            selectedValues={Array.isArray(eventId.value) ? eventId.value.filter(x => x !== String(event_id)).map(v => `id:${v}`) : `id:${eventId.value}`}
                            value={queryTargetRef.current === 'events' ? keyword : ''}
                            placeholder={(Array.isArray(eventId.value) && eventId.value.filter(x => String(x) !== event_id)).length > 0 ? renderPlaceholderSuggest(eventId) : eventId.placeholder}
                            data={Object.fromEntries((events.data || []).filter(x => String(x.id) !== event_id).map(e => [`id:${e.id}`, e.eventTitle]))}
                            onInput={({target}) => setKeyword(target.value || '')}
                            onChange={(val) => {
                                const mapped = Array.isArray(val)
                                    ? val.filter(v => v && v !== 'id:null').map(v => v.replace(/^id:/, ''))
                                    : val.replace(/^id:/, '');
                                handleChange('eventId', mapped);
                                setKeyword('');
                                handleGetLists({key: 'events', fn: EventBasesService.GetList, num: 1});
                            }}
                            onReachEnd={() => handleReachEnd('events')}
                            onDropShow={() => {
                                queryTargetRef.current = 'events';
                                setKeyword('');
                            }}
                            onDropHide={() => {
                                setKeyword('');
                                queryTargetRef.current = null;
                            }}
                        />
                    </div>
                    {content === 'promocodes' && <div className={styles.field}>
                        <Checkbox
                            size={'large'}
                            className={styles.checkbox}
                            label={active.label}
                            error={active.error}
                            value={(active.value ?? false) as boolean}
                            disabled={isLoading || active.isDisabled || active.isLoading}
                            onChange={(value) => handleChange('active', value || null)}>
                            Только активные
                        </Checkbox>
                    </div>}
                </div>
                <div className={styles.show}>
                    <Button
                        size={'small'}
                        type={'secondary'}
                        theme={'default'}
                        disabled={isLoading}
                        htmlType={'submit'}>
                        Показать
                    </Button>
                    <Button
                        size={'small'}
                        type={'tertiary'}
                        theme={'default'}
                        className={styles.cross}
                        disabled={isLoading}
                        onClick={handleReset}
                        htmlType={'reset'}>
                        <CrossIcon/>
                    </Button>
                </div>
            </form>
        );
    }
;

export default Filter;
